import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil, startWith } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import {
	ConfirmationDialogComponent,
	ConfirmationDialogData,
} from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';

import {
	OperationType,
	UploadStatus,
	UploadStatusFilter,
} from '../../../../shared/enums/batch-actions.enum';
import {
	BatchAction,
	UPLOAD_STATUS_LABEL,
} from '../../models/batch-actions.model';
import {
	fetchOrgBatchActionFileClear,
	fetchOrgBatchActionFileStart,
	fetchOrgBatchActionsStart,
	cancelOrgBatchActionStart,
} from '../../state/organizations.actions';
import {
	selectOrgBatchActions,
	selectOrgBatchActionFileUrl,
	selectOrgLoading,
} from '../../state/organizations.selector';

export const OPERATION_TYPE_LABEL = [
	{ label: 'Criação', type: OperationType.CREATION },
	{ label: 'Atualização', type: OperationType.UPDATE },
	{ label: 'Exclusão', type: OperationType.EXCLUSION },
	{ label: 'Transferência', type: OperationType.TRANSFER },
	{ label: 'Transferência Programada', type: OperationType.SCHEDULED_TRANSFER },
	{ label: 'Reconciliação', type: OperationType.RECONCILIATION },
	{ label: 'Outro', type: OperationType.OTHER },
	{ label: 'Padrão', type: OperationType.DEFAULT },
];

@Component({
	selector: 'app-batch-actions',
	styleUrls: ['./batch-actions.component.scss'],
	templateUrl: './batch-actions.component.html',
})
export class BatchActionsComponent implements OnInit, OnDestroy {
	batchActions$: Observable<BatchAction[] | null>;
	filterOptions = [
		{ label: 'Todos', value: UploadStatusFilter.ALL },
		{ label: 'Sucesso', value: UploadStatusFilter.SUCCESS },
		{ label: 'Falha', value: UploadStatusFilter.FAILURE },
		{ label: 'Processando', value: UploadStatusFilter.IN_PROCESS },
		{ label: 'Agendado', value: UploadStatusFilter.SCHEDULED },
		{ label: 'Cancelado', value: UploadStatusFilter.CANCELLED },
	];

	isLoading$: Observable<boolean>;
	operationFilterControl = new FormControl('all');

	operationFilterOptions = [
		{ label: 'Todas', value: 'all' },
		{ label: 'Criação', value: OperationType.CREATION },
		{ label: 'Atualização', value: OperationType.UPDATE },
		{ label: 'Exclusão', value: OperationType.EXCLUSION },
		{ label: 'Transferência', value: OperationType.TRANSFER },
		{
			label: 'Transferência Programada',
			value: OperationType.SCHEDULED_TRANSFER,
		},
		{ label: 'Reconciliação', value: OperationType.RECONCILIATION },
		{ label: 'Outro', value: OperationType.OTHER },
		{ label: 'Padrão', value: OperationType.DEFAULT },
	];

	orgId: string = '';
	preventMultipleDownloads = false;
	statusFilterControl = new FormControl<UploadStatusFilter>(
		UploadStatusFilter.ALL,
	);
	uploadStatus = UploadStatus;

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly router: Router,
		private readonly store: Store,
		private readonly dialog: MatDialog,
	) {
		this.batchActions$ = this.store.select(selectOrgBatchActions);
		this.isLoading$ = this.store.select(selectOrgLoading);
	}

	batchActionOperationLabel(batchAction: BatchAction): string {
		return (
			batchAction.operationLabel ||
			this.operationTypeLabel(batchAction.operationType)
		);
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	ngOnInit(): void {
		this.store.dispatch(fetchOrgBatchActionFileClear());

		this.activatedRoute.params
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(({ id }) => {
				this.orgId = id;
			});

		combineLatest([
			this.statusFilterControl.valueChanges.pipe(
				startWith(this.statusFilterControl.value),
			),
			this.operationFilterControl.valueChanges.pipe(
				startWith(this.operationFilterControl.value),
			),
		])
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(([status, operationType]) => {
				const effectiveStatus: UploadStatusFilter =
					status ?? UploadStatusFilter.ALL;
				const effectiveOperation: string = operationType ?? 'all';
				this.fetchData(effectiveStatus, effectiveOperation);
			});

		this.store
			.select(selectOrgBatchActionFileUrl)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((url) => {
				if (url && !this.preventMultipleDownloads) {
					this.preventMultipleDownloads = true;
					const link = document.createElement('a');
					link.setAttribute('href', url);
					document.body.appendChild(link);
					link.click();
					link.remove();
					this.store.dispatch(fetchOrgBatchActionFileClear());
				}
			});
	}

	onCancelSchedule(id: string, event: Event): void {
		event.stopPropagation();
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			data: {
				confirmButtonText: 'Cancelar transferência',
				message:
					'Tem certeza de que deseja cancelar esta transferência agendada? Esta ação não pode ser desfeita.',
				title: 'Cancelar transferência agendada',
			} as ConfirmationDialogData,
			disableClose: true,
		});
		dialogRef.afterClosed().subscribe((confirmed: boolean) => {
			if (confirmed) {
				this.store.dispatch(cancelOrgBatchActionStart({ id }));
			}
		});
	}

	onDownloadFile(key: string, event: Event): void {
		event.stopPropagation();
		this.preventMultipleDownloads = false;
		this.store.dispatch(fetchOrgBatchActionFileStart({ id: key }));
	}

	onViewDetails(id: string): void {
		this.router.navigate(['organizacoes', this.orgId, 'acoes-em-massa', id]);
	}

	operationTypeLabel(type: OperationType): string {
		return (
			OPERATION_TYPE_LABEL.find((ot) => ot.type === type)?.label ??
			'Desconhecido'
		);
	}

	statusLabel(status: UploadStatus): string {
		return UPLOAD_STATUS_LABEL.find((us) => us.status === status)!.label;
	}

	private fetchData(
		status: UploadStatusFilter,
		operationType: string = 'all',
	): void {
		if (this.orgId) {
			this.store.dispatch(
				fetchOrgBatchActionsStart({
					operationType:
						operationType !== 'all'
							? (operationType as OperationType)
							: undefined,
					orgId: this.orgId,
					status,
				}),
			);
		}
	}
}
